/**
 * Zentraler Katalog aller Dienstleistungs-Seiten unter /dienstleistungen.
 *
 * EINE Quelle der Wahrheit für Slugs, Namen und die Standard-Sichtbarkeit.
 * Die öffentliche Sichtbarkeit wird im Admin (Admin → Dienstleistungen)
 * geschaltet und in `site_settings[service_visibility]` gespeichert; der
 * Katalog liefert den Default, falls dort (noch) nichts steht.
 *
 * Client-safe: keine Server-Imports, damit Header/Footer/ServicesPreview
 * (Client-Komponenten) die Filter-Helfer nutzen können.
 */
export type ServiceDef = {
  slug: string;
  name: string;
  href: string;
  /** Sichtbar, solange im Admin nichts anderes gesetzt ist. */
  defaultVisible: boolean;
};

export const SERVICE_VISIBILITY_KEY = "service_visibility";

export const SERVICE_CATALOG: ServiceDef[] = [
  { slug: "webdesign", name: "Website-Entwicklung", href: "/dienstleistungen/webdesign", defaultVisible: true },
  { slug: "seo", name: "Suchmaschinen-Optimierung", href: "/dienstleistungen/seo", defaultVisible: true },
  { slug: "social-media-marketing", name: "Social Media Marketing", href: "/dienstleistungen/social-media-marketing", defaultVisible: true },
  { slug: "branding", name: "Markenidentität", href: "/dienstleistungen/branding", defaultVisible: true },
  { slug: "software-entwicklung", name: "Software & App-Entwicklung", href: "/dienstleistungen/software-entwicklung", defaultVisible: true },
  { slug: "paid-ads", name: "Online-Werbung", href: "/dienstleistungen/paid-ads", defaultVisible: true },
  { slug: "wartungsservice", name: "Wartung & Hosting", href: "/dienstleistungen/wartungsservice", defaultVisible: true },
  { slug: "domains", name: "Domain-Verwaltung", href: "/dienstleistungen/domains", defaultVisible: true },
  // Neu (2026-09): bewusst NICHT öffentlich, bis im Admin freigeschaltet.
  { slug: "ki-schulungen", name: "KI-Schulungen", href: "/dienstleistungen/ki-schulungen", defaultVisible: false },
  // Neu (2026-10): Analysetag vor Ort – versteckt, bis Inhalte freigegeben sind.
  { slug: "ki-analyse", name: "KI-Potenzialanalyse", href: "/dienstleistungen/ki-analyse", defaultVisible: false },
  // Neu (2026-10-28, Paket 2): Erklärvideos – versteckt, Freischaltung im Admin nach Abnahme.
  { slug: "motion-design", name: "Motion Design & Erklärvideos", href: "/dienstleistungen/motion-design", defaultVisible: false },
];

export function isServiceSlug(slug: string): boolean {
  return SERVICE_CATALOG.some((s) => s.slug === slug);
}

/** Letztes Pfadsegment eines Dienstleistungs-Links → Slug. */
export function serviceSlugFromHref(href: string): string {
  return href.replace(/\/+$/, "").split("/").pop() ?? "";
}

/**
 * Filtert eine beliebige Link-Liste (Header, Footer, Karten) auf die
 * öffentlich sichtbaren Dienstleistungen. Ohne `visibleSlugs` (z. B. wenn
 * der Server nichts mitgegeben hat) greift die Katalog-Default-Sichtbarkeit
 * – so bleibt eine neue, noch nicht freigeschaltete Seite auch dann
 * verborgen.
 */
export function filterByVisibleSlugs<T extends { href: string }>(
  items: T[],
  visibleSlugs?: string[],
): T[] {
  const allowed = new Set(
    visibleSlugs ??
      SERVICE_CATALOG.filter((s) => s.defaultVisible).map((s) => s.slug),
  );
  return items.filter((item) => {
    const slug = serviceSlugFromHref(item.href);
    // Links, die keine Katalog-Seite sind, bleiben unangetastet.
    if (!isServiceSlug(slug)) return true;
    return allowed.has(slug);
  });
}
